import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import {
	Plus,
	Pencil,
	Trash2,
	Shield,
	Users,
	Eye,
	Loader2,
	Lock,
	AlertTriangle,
	SlidersHorizontal,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";

import MainContainer from "../../components/MainContainer";
import PageTabs from "../../components/PageTabs";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";

// O escopo é o conceito mais difícil da tela: cada opção ganha um exemplo do
// dia a dia, e a que costuma pegar as pessoas de surpresa vem com aviso.
const ESCOPOS = [
	{
		value: "own",
		rotulo: "Só os próprios",
		ajuda: "Vê apenas os atendimentos que estão com ele.",
		exemplo: "Como um atendente que só cuida de quem o gerente passa para ele.",
		aviso:
			"Com esta opção a pessoa não recebe conversa nova da fila sozinha — alguém precisa passar para ela.",
	},
	{
		value: "team",
		rotulo: "Da equipe",
		ajuda: "Vê os atendimentos de quem está na mesma equipe.",
		exemplo: "Como uma dupla que se cobre quando um sai para almoçar.",
	},
	{
		value: "queues",
		rotulo: "Das filas dele",
		ajuda: "Vê tudo que cai nas filas em que ele participa.",
		exemplo: "Como quem atende o Suporte: pega qualquer conversa que chega ali.",
		recomendado: true,
	},
	{
		value: "company",
		rotulo: "Todos da empresa",
		ajuda: "Vê qualquer atendimento, de qualquer pessoa.",
		exemplo: "Como o dono, que precisa enxergar a operação inteira.",
	},
];

// Modelos prontos para quem está criando o primeiro cargo. Encarar 61
// caixinhas em branco é onde a maioria desiste.
const MODELOS = [
	{
		id: "atendente",
		nome: "Atendente",
		resumo: "Responde as conversas que chegam. Não mexe em configuração.",
		ticketScope: "queues",
		baseSlug: "user",
	},
	{
		id: "supervisor",
		nome: "Supervisor",
		resumo:
			"Acompanha a equipe, vê relatórios e transfere conversas entre atendentes.",
		ticketScope: "queues",
		baseSlug: "supervisor",
	},
	{
		id: "zero",
		nome: "Começar do zero",
		resumo: "Nenhuma permissão marcada. Você escolhe uma a uma.",
		ticketScope: "own",
		baseSlug: null,
	},
];

// Traduz o cargo para uma frase que se lê sem saber o que é "permissão".
const descreverCargo = (cargo, catalogo) => {
	if (!cargo) return "";

	const tem = new Set((cargo.permissions || []).map((p) => p.permission));

	const escopo = {
		own: "vê só os atendimentos que estão com ela",
		team: "vê os atendimentos da equipe dela",
		queues: "vê os atendimentos das filas em que participa",
		company: "vê todos os atendimentos da empresa",
	}[cargo.ticketScope];

	const podeResponder = tem.has("messages:send");
	const podeEncerrar = tem.has("tickets:close");
	const mexeEmConfig = [...tem].some(
		(chave) =>
			chave.startsWith("system:") ||
			chave.startsWith("users:") ||
			chave.startsWith("connections:"),
	);

	const acoes = [];
	if (podeResponder) acoes.push("responder");
	if (podeEncerrar) acoes.push("encerrar conversas");

	const total = catalogo.reduce((n, g) => n + g.permissions.length, 0);

	return [
		`Quem tem este cargo ${escopo}`,
		acoes.length ? ` e pode ${acoes.join(" e ")}` : "",
		". ",
		mexeEmConfig
			? "Também mexe nas configurações do sistema."
			: "Não mexe nas configurações do sistema.",
		total ? ` (${tem.size} de ${total} permissões)` : "",
	].join("");
};

const Roles = () => {
	const [roles, setRoles] = useState([]);
	const [catalogo, setCatalogo] = useState([]);
	const [carregando, setCarregando] = useState(true);
	const [salvando, setSalvando] = useState(false);

	const [aba, setAba] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [form, setForm] = useState({});
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [selecionado, setSelecionado] = useState(null);
	// As permissões item a item ficam guardadas: quem só quer um cargo de
	// atendente não deveria ter de encarar 61 caixinhas para consegui-lo.
	const [detalhesAbertos, setDetalhesAbertos] = useState(false);

	const carregar = useCallback(async () => {
		try {
			const [lista, cat] = await Promise.all([
				api.get("/roles"),
				api.get("/permissions/catalog"),
			]);
			setRoles(lista.data);
			setCatalogo(cat.data);
			setAba((atual) => atual || String(lista.data[0]?.id || ""));
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const cargoAtual = roles.find((r) => String(r.id) === String(aba));

	const permissoesDoCargo = new Set(
		(cargoAtual?.permissions || []).map((p) => p.permission)
	);

	const ehAdmin = cargoAtual?.slug === "admin";

	const alternar = async (chave) => {
		if (!cargoAtual) return;

		const novas = permissoesDoCargo.has(chave)
			? [...permissoesDoCargo].filter((p) => p !== chave)
			: [...permissoesDoCargo, chave];

		setSalvando(true);
		try {
			await api.put(`/roles/${cargoAtual.id}`, { permissions: novas });
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const alternarGrupo = async (permissoes, marcarTodas) => {
		if (!cargoAtual) return;

		const chavesDoGrupo = permissoes.map((p) => p.key);

		const novas = marcarTodas
			? [...new Set([...permissoesDoCargo, ...chavesDoGrupo])]
			: [...permissoesDoCargo].filter((p) => !chavesDoGrupo.includes(p));

		setSalvando(true);
		try {
			await api.put(`/roles/${cargoAtual.id}`, { permissions: novas });
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const definirEscopo = async (escopo) => {
		setSalvando(true);
		try {
			await api.put(`/roles/${cargoAtual.id}`, { ticketScope: escopo });
			toast.success("Escopo atualizado.");
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const salvarCargo = async () => {
		setSalvando(true);
		try {
			// O modelo entra como ponto de partida: copia as permissões de um
			// cargo que já existe, em vez de nascer com nenhuma.
			const modelo = MODELOS.find((m) => m.id === form.modelo);
			const base = modelo?.baseSlug
				? roles.find((r) => r.slug === modelo.baseSlug)
				: null;

			await api.post("/roles", {
				name: form.name,
				description: form.description,
				ticketScope: form.ticketScope || modelo?.ticketScope || "own",
				permissions: (base?.permissions || []).map((p) => p.permission),
			});
			toast.success("Cargo criado.");
			setModalOpen(false);
			setForm({});
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const excluir = async () => {
		try {
			await api.delete(`/roles/${selecionado.id}`);
			toast.success("Cargo excluído.");
			setAba(null);
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSelecionado(null);
	};

	if (carregando) {
		return (
			<MainContainer>
				<div className="flex flex-1 items-center justify-center">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			</MainContainer>
		);
	}

	return (
		<MainContainer>
			<ConfirmationModal
				title={selecionado && `Excluir o cargo ${selecionado.name}?`}
				open={confirmOpen}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionado(null);
				}}
				onConfirm={excluir}
				confirmLabel="Excluir cargo"
			>
				Quem estiver com este cargo fica sem permissão nenhuma até você
				escolher outro. Cargos em uso não podem ser excluídos.
			</ConfirmationModal>

			<div className="flex flex-wrap items-center gap-2 px-4 py-3 sm:px-6">
				<div className="min-w-0 flex-1">
					<h1 className="truncate text-lg font-semibold tracking-tight">
						Cargos e permissões
					</h1>
					<p className="text-xs text-muted-foreground">
						Defina o que cada tipo de usuário pode fazer
					</p>
				</div>
				<Button
					className="h-9"
					onClick={() => {
						setForm({
							name: "",
							description: "",
							modelo: "atendente",
							ticketScope: "queues",
						});
						setDetalhesAbertos(false);
						setModalOpen(true);
					}}
				>
					<Plus className="h-4 w-4" />
					<span className="hidden sm:inline">Novo cargo</span>
					<span className="sm:hidden">Novo</span>
				</Button>
			</div>

			<PageTabs
				abas={roles.map((r) => ({
					value: String(r.id),
					rotulo: r.name,
					icone: r.isSystem ? Shield : Users,
					contador: r.userCount ?? 0,
				}))}
				atual={aba}
				onChange={setAba}
			/>

			{cargoAtual && (
				<div className="flex-1 space-y-4 overflow-auto p-4 sm:p-6">
					{/* identificação do cargo */}
					<section className="rounded-xl border bg-card p-4">
						<div className="flex flex-wrap items-start justify-between gap-3">
							<div className="min-w-0">
								<div className="flex items-center gap-2">
									<h2 className="text-base font-semibold">
										{cargoAtual.name}
									</h2>
									{cargoAtual.isSystem && (
										<span className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
											<Lock className="h-3 w-3" />
											Cargo do sistema
										</span>
									)}
								</div>
								<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
									{cargoAtual.description || "Sem descrição."}
								</p>
								{/* A tradução do cargo para uma frase: é o que se lê
								    sem saber o que é "permissão". */}
								<p className="mt-2 text-sm leading-relaxed">
									{descreverCargo(cargoAtual, catalogo)}
								</p>
								<p className="mt-1 text-xs text-muted-foreground">
									{cargoAtual.userCount === 0
										? "Ninguém usa este cargo ainda."
										: cargoAtual.userCount === 1
											? "1 pessoa usa este cargo."
											: `${cargoAtual.userCount} pessoas usam este cargo.`}
								</p>
							</div>

							{!cargoAtual.isSystem && (
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8 shrink-0"
									aria-label={`Excluir ${cargoAtual.name}`}
									onClick={() => {
										setSelecionado(cargoAtual);
										setConfirmOpen(true);
									}}
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							)}
						</div>
					</section>

					{/* escopo de visualização */}
					<section className="rounded-xl border bg-card">
						<header className="flex items-start gap-3 border-b p-4">
							<span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
								<Eye className="h-4 w-4 text-muted-foreground" />
							</span>
							<div>
								<h3 className="text-sm font-semibold">
									Quais atendimentos ele enxerga
								</h3>
								<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
									Vale mesmo que o usuário marque “ver de toda a equipe” na tela
									de atendimentos.
								</p>
							</div>
						</header>

						<div className="grid gap-2 p-4 sm:grid-cols-2">
							{ESCOPOS.map((escopo) => {
								const ativo = cargoAtual.ticketScope === escopo.value;
								return (
									<button
										key={escopo.value}
										type="button"
										disabled={salvando}
										onClick={() => definirEscopo(escopo.value)}
										className={cn(
											"rounded-lg border p-3 text-left transition-colors",
											ativo
												? "border-primary bg-primary/5"
												: "hover:bg-muted"
										)}
									>
										<p className="flex items-center gap-2 text-sm font-medium">
											{escopo.rotulo}
											{escopo.recomendado && (
												<span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
													mais usado
												</span>
											)}
										</p>
										<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
											{escopo.ajuda}
										</p>
										<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
											{escopo.exemplo}
										</p>
										{ativo && escopo.aviso && (
											<p className="mt-2 flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
												<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
												{escopo.aviso}
											</p>
										)}
									</button>
								);
							})}
						</div>
					</section>

					{/* permissões, guardadas por padrão */}
					<button
						type="button"
						onClick={() => setDetalhesAbertos((v) => !v)}
						className="flex w-full items-center gap-3 rounded-xl border bg-card p-4 text-left transition-colors hover:bg-muted/50"
					>
						<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
							<SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
						</span>
						<span className="min-w-0 flex-1">
							<span className="block text-sm font-semibold">
								Ajustar item por item
							</span>
							<span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
								Para casos específicos. Se o resumo acima já descreve o que
								você quer, não precisa abrir.
							</span>
						</span>
						<span className="shrink-0 text-xs text-muted-foreground">
							{detalhesAbertos ? "Fechar" : "Abrir"}
						</span>
					</button>

					{detalhesAbertos &&
					catalogo.map((grupo) => {
						// Permissão de admin não vale para outros cargos: ela aparece
						// travada em vez de marcável, para a tela não mentir.
						const aplicaveis = grupo.permissions.filter(
							(p) => !p.adminOnly || ehAdmin
						);
						const marcadas = aplicaveis.filter((p) =>
							permissoesDoCargo.has(p.key)
						).length;
						const todas =
							aplicaveis.length > 0 && marcadas === aplicaveis.length;

						return (
							<section key={grupo.group} className="rounded-xl border bg-card">
								<header className="flex flex-wrap items-center justify-between gap-2 border-b p-3 px-4">
									<div className="flex items-center gap-2">
										<h3 className="text-sm font-semibold">{grupo.group}</h3>
										<span className="text-xs text-muted-foreground">
											{marcadas}/{aplicaveis.length}
										</span>
									</div>
									<Button
										variant="ghost"
										size="sm"
										className="h-7 text-xs"
										disabled={salvando || aplicaveis.length === 0}
										onClick={() => alternarGrupo(aplicaveis, !todas)}
									>
										{todas ? "Desmarcar todas" : "Marcar todas"}
									</Button>
								</header>

								<div className="divide-y">
									{grupo.permissions.map((permissao) => {
										const marcada = permissoesDoCargo.has(permissao.key);
										const travada = permissao.adminOnly && !ehAdmin;

										return (
											<button
												key={permissao.key}
												type="button"
												disabled={salvando || travada}
												onClick={() => alternar(permissao.key)}
												title={
													travada
														? "Só administradores podem ter esta permissão"
														: undefined
												}
												className={cn(
													"flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors",
													travada
														? "cursor-not-allowed opacity-50"
														: "hover:bg-muted/50 disabled:opacity-60"
												)}
											>
												<span
													className={cn(
														"flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
														marcada && !travada
															? "border-primary bg-primary"
															: "border-input"
													)}
												>
													{marcada && !travada && (
														<svg
															viewBox="0 0 12 12"
															className="h-3 w-3 text-primary-foreground"
															fill="none"
															stroke="currentColor"
															strokeWidth="2"
														>
															<path d="M2 6l3 3 5-6" />
														</svg>
													)}
												</span>
												<span className="min-w-0 flex-1 text-sm">
													{permissao.label}
												</span>
												{travada && (
													<span className="flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
														<Lock className="h-3 w-3" />
														Só admin
													</span>
												)}
											</button>
										);
									})}
								</div>
							</section>
						);
					})}
				</div>
			)}

			<Dialog open={modalOpen} onOpenChange={setModalOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Novo cargo</DialogTitle>
						<DialogDescription>
							Escolha algo parecido com o que você precisa. Dá para mudar
							qualquer detalhe depois.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-3">
						<div className="space-y-1.5">
							<Label>Parecido com</Label>
							<div className="space-y-1.5">
								{MODELOS.map((modelo) => (
									<button
										key={modelo.id}
										type="button"
										onClick={() =>
											setForm({
												...form,
												modelo: modelo.id,
												ticketScope: modelo.ticketScope,
											})
										}
										className={cn(
											"w-full rounded-lg border p-2.5 text-left transition-colors",
											form.modelo === modelo.id
												? "border-primary bg-primary/5"
												: "hover:bg-muted"
										)}
									>
										<p className="text-sm font-medium">{modelo.nome}</p>
										<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
											{modelo.resumo}
										</p>
									</button>
								))}
							</div>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="nomeCargo">Como vai se chamar</Label>
							<Input
								id="nomeCargo"
								placeholder="Ex.: Atendente da noite"
								value={form.name || ""}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
							/>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="descCargo">
								Para que serve{" "}
								<span className="font-normal text-muted-foreground">
									(opcional)
								</span>
							</Label>
							<Textarea
								id="descCargo"
								rows={2}
								placeholder="Uma linha para você lembrar depois"
								value={form.description || ""}
								onChange={(e) =>
									setForm({ ...form, description: e.target.value })
								}
							/>
						</div>
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setModalOpen(false)}>
							Cancelar
						</Button>
						<Button disabled={salvando || !form.name} onClick={salvarCargo}>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							Criar cargo
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</MainContainer>
	);
};

export default Roles;
